/**
 * WHO MAY DO WHAT IN THE FIRM (spec docs/specs/bms/2026-10-26-001-permissions-page.md §2).
 *
 * One entry per action a firm's admin can do and a member may not, plus the
 * actions anyone may do that a member might think are an admin's. The server's
 * refusals (`requirePermission` in `principal.ts`), the screens' gates
 * (`useMayDo`) and the Permissions page all read this list, so the page can
 * never say "Everyone" about something the server refuses, and the boundary
 * ratchet (`src/__tests__/boundaries/bms-admin-gate-ratchet.test.ts`) refuses
 * an admin gate with no entry here and an entry with no gate.
 *
 * BROWSER-SAFE AND PURE, as `firm-switches.ts` beside it is: it imports nothing
 * that runs, and `FirmSwitchKey` only as a type.
 *
 * A KEY IS NEVER RENAMED. The editable version stores a firm's override
 * against it (spec §4). `who` is the default every firm starts from.
 *
 * `refusal` is the sentence the server sends today, word for word, and only
 * the keys `requirePermission` refuses carry one. The local checks (the audit's
 * undo, the reference mapping, the outbound send and review, the money keep)
 * keep their own sentences beside their own rules and ask `mayDo`.
 *
 * DECISION 048 IS WRITTEN HERE. What only a firm admin can do (decision
 * `docs/BMS/decisions/2026-10/048-what-only-a-firm-admin-can-do.md`) is these
 * rows' defaults: shaping the firm is an admin's, work on a record a person
 * can see is everyone's. A service hands a gated method out through
 * `withPermission(key, method)` in `principal.ts`, and
 * `src/__tests__/boundaries/bms-firm-admin-gate-ratchet.test.ts` calls each one
 * as a member, a firm key and a job.
 */
import type { FirmSwitchKey } from "./firm-switches"

export type FirmPermissionWho = "admins" | "everyone"

/** The page's sections, in the order it draws them. */
export type FirmPermissionArea =
  | "registers"
  | "firmSettings"
  | "people"
  | "documents"
  | "audit"
  | "workflows"
  | "sync"
  | "move"
  | "jobs"

export interface FirmPermission {
  /** Stable forever: an override is stored against it. */
  readonly key: string
  readonly area: FirmPermissionArea
  /** The action, verb first, named as the screens name it. */
  readonly label: string
  /** The default. */
  readonly who: FirmPermissionWho
  /** The firm's own admin only: Cova staff acting in the firm are refused. */
  readonly admin?: "firm"
  /** A firm key or a job also passes: the sweeps. */
  readonly keysAndJobs?: true
  /**
   * A firm key holding this write scope also passes (decision 048 ruling 2):
   * the area is published on the public lane under it, and an admin chose the
   * scope when issuing the key. A job never passes through it, and a key
   * without the scope is refused as a member is.
   */
  readonly keyScope?: `bms:${string}:write`
  /** The server's refusal, word for word, where `requirePermission` is the gate. */
  readonly refusal?: string
  /**
   * What a member may still do beside an Admins row, by a rule of its own
   * outside `mayDo`, so the page never reads as "a member can never". One
   * sentence, drawn under the label.
   */
  readonly exception?: string
  /** Hidden on the page while this switch is off. */
  readonly needsSwitch?: FirmSwitchKey
}

// The suggest sentence comes back with the catalogue change-request flow; until
// then there is nowhere to suggest a change, so the refusal does not offer it.
const REGISTERS_REFUSAL = "Only an administrator can change the firm's registers."
const MARKETS_REFUSAL = "Only an administrator can change the firm's panel of markets."
const SETTINGS_REFUSAL = "Only an administrator can change the firm's settings."
const SYNC_CONNECTION_REFUSAL = "Only a firm administrator can change the sync connection."
const SYNC_VIEWS_REFUSAL = "The sync's status is an admin's page. Ask an administrator."

export const FIRM_PERMISSIONS = [
  // ─── Registers ──────────────────────────────────────────────────────────
  {
    key: "registers.entries",
    area: "registers",
    who: "admins",
    label: "Add, rename, retire, restore, reorder, tag or hide a list's entries",
    refusal: REGISTERS_REFUSAL,
  },
  {
    key: "registers.presentation",
    area: "registers",
    who: "admins",
    label: "Change how a list is drawn: its colours and icons",
    refusal: "Only an administrator can change how the firm's catalogues are drawn.",
  },
  {
    key: "markets.change",
    area: "registers",
    who: "admins",
    label: "Edit a market, change its status, withdraw a request or settle a synced field on it",
    refusal: MARKETS_REFUSAL,
  },
  {
    key: "markets.add",
    area: "registers",
    who: "everyone",
    label: "Add a market from the catalogue, or ask for a new insurer",
  },
  {
    key: "registers.records",
    area: "registers",
    who: "everyone",
    label: "Add or edit contacts and referral partners, and create or assign tags",
  },
  // Decision 048 ruling 2: firm structure, an admin's for members; a firm key
  // keeps the write scope it is published under.
  {
    key: "registers.branches",
    area: "registers",
    who: "admins",
    keyScope: "bms:branches:write",
    label: "Add, rename, archive or restore a branch",
    refusal: "Only an administrator can change the firm's branches.",
  },
  {
    key: "registers.authorisedReps",
    area: "registers",
    who: "admins",
    keyScope: "bms:authorised-reps:write",
    label: "Add, edit, archive or restore an authorised representative",
    refusal: "Only an administrator can change the firm's authorised representatives.",
  },
  // Anyone creates, assigns and unassigns a tag (`registers.records`).
  {
    key: "registers.tags",
    area: "registers",
    who: "admins",
    label: "Rename, recolour, reorder, merge or delete a tag",
    refusal: "Only an administrator can change the firm's tags.",
  },
  {
    key: "registers.tagRules",
    area: "registers",
    who: "admins",
    label: "Create, edit, delete or run a tag rule",
    refusal: "Only an administrator can change the firm's tag rules.",
  },
  // Decision 048 ruling 3: anyone adds and edits a partner; what the firm pays
  // out, and who stays on the roster, is an admin's.
  {
    key: "registers.referralAgreements",
    area: "registers",
    who: "admins",
    label: "Change what a referral partner is paid",
    refusal: "Only an administrator can change the firm's referral agreements.",
  },
  {
    key: "registers.referralPartners",
    area: "registers",
    who: "admins",
    label: "Retire, reinstate or delete a referral partner",
    refusal: "Only an administrator can change the firm's referral partners.",
  },
  {
    key: "registers.referralFees",
    area: "registers",
    who: "admins",
    label: "Approve, pay or waive a referral fee",
    refusal: "Only an administrator can change the firm's referral fees.",
  },
  {
    key: "registers.dateTypes",
    area: "registers",
    who: "admins",
    label: "Add a type of important date",
    refusal: "Only an administrator can change the firm's important date types.",
  },

  // ─── Firm settings: four keys over `FirmPreferencesService.update` ──────
  {
    key: "firmSettings.renewalWindow",
    area: "firmSettings",
    who: "admins",
    label: "Change the renewal notice window",
    refusal: SETTINGS_REFUSAL,
  },
  {
    key: "firmSettings.letterWording",
    area: "firmSettings",
    who: "admins",
    label: "Write or edit the renewal letter wording",
    refusal: SETTINGS_REFUSAL,
  },
  {
    key: "firmSettings.bookOpen",
    area: "firmSettings",
    who: "admins",
    label: "Open or close the book to every member",
    refusal: SETTINGS_REFUSAL,
  },
  {
    key: "firmSettings.glyphDisplay",
    area: "firmSettings",
    who: "admins",
    label: "Choose when chips show their list's icon",
    refusal: SETTINGS_REFUSAL,
  },
  // Decision 048 ruling (f): published on the public lane under the renewals
  // write scope, so a firm key holding it keeps the write.
  {
    key: "firmSettings.chaseCadences",
    area: "firmSettings",
    who: "admins",
    keyScope: "bms:renewals:write",
    label: "Change how often a renewal is chased",
    refusal: "Only an administrator can change the firm's chase cadences.",
  },

  // ─── People ─────────────────────────────────────────────────────────────
  // `people.teams` is gated outside the BMS contexts (`OrgAdminMiddleware` on
  // the teams-admin RPC), so it carries no refusal here.
  {
    key: "people.teams",
    area: "people",
    who: "admins",
    label: "Manage teams and who is on them",
    exception: "A team's lead may also add and remove the members of their own team, but not change roles.",
  },
  // Decision 048: who sees which client is the firm's to decide. A member
  // still shares a client with their own teams from its create or edit
  // (ruling 4), which asks `people.clientMoves` only for any other team.
  {
    key: "people.allocations",
    area: "people",
    who: "admins",
    label: "Create, edit, switch on or off, reorder, apply or delete allocation rules",
    refusal: "Only an administrator can change the firm's allocation rules.",
  },
  {
    key: "people.clientMoves",
    area: "people",
    who: "admins",
    label: "Move clients between teams, set who looks after a client, or give or take away access to a client",
    refusal: "Only an administrator can change the firm's client allocations.",
    exception: "Anyone may share a client they add or edit with the teams they are on.",
  },
  {
    key: "people.targets",
    area: "people",
    who: "admins",
    label: "Set, change or remove targets",
    refusal: "Only an administrator can change the firm's targets.",
  },

  // ─── Documents: gated in the files context, so no refusal here ──────────
  {
    key: "documents.activity",
    area: "documents",
    who: "admins",
    label: "See the firm's document activity and who holds which folder",
  },
  {
    key: "documents.folders",
    area: "documents",
    who: "admins",
    admin: "firm",
    label: "Manage who can open firm folders, or create a library shelf",
  },
  // No "Delete forever from the trash" row (`documents.deleteForever`): no file
  // is destroyed anywhere a firm reaches (T-27, Tai 2026-11-01), so there is
  // nothing to permit. `mayDo` refuses the key as one this build does not hold,
  // so an override stored against it is ignored, never an error.

  // ─── Audit ──────────────────────────────────────────────────────────────
  // `AuditService.requireReverter` keeps its own two sentences.
  {
    key: "audit.undo",
    area: "audit",
    who: "admins",
    label: "Undo a change, or restore an earlier version",
  },

  // ─── Workflows and boards ───────────────────────────────────────────────
  {
    key: "workflows.defaults",
    area: "workflows",
    who: "admins",
    label: "Add Cova's default workflows",
    refusal:
      "Adding Cova's default workflows is an administrator action, because it changes the firm's workflow library.",
    needsSwitch: "workflows",
  },
  {
    key: "workflows.templates",
    area: "workflows",
    who: "admins",
    label: "Create, copy, edit, lay out, archive, restore or delete a workflow",
    refusal: "Only an administrator can change the firm's workflows.",
    needsSwitch: "workflows",
  },
  {
    key: "workflows.columns",
    area: "workflows",
    who: "admins",
    label: "Add, edit, remove, reorder or reset a board's columns",
    refusal: "Only an administrator can change the firm's boards.",
  },
  {
    key: "workflows.lostReasons",
    area: "workflows",
    who: "admins",
    label: "Add, edit or delete lost reasons",
    refusal: "Only an administrator can change the firm's lost reasons.",
  },

  // ─── Steadfast Apps sync ────────────────────────────────────────────────
  {
    key: "sync.connection",
    area: "sync",
    who: "admins",
    label: "Connect, check, pause, resume, sign in again, disconnect, reconnect or refresh the sync, and choose what crosses",
    refusal: SYNC_CONNECTION_REFUSAL,
    needsSwitch: "sync",
  },
  {
    key: "sync.sending",
    area: "sync",
    who: "admins",
    label: "Turn sending to Steadfast Apps on or off, and choose how it sends",
    refusal: SYNC_CONNECTION_REFUSAL,
    needsSwitch: "sync",
  },
  {
    key: "sync.held",
    area: "sync",
    who: "admins",
    label: "Retry held items and confirm held deletes",
    refusal: SYNC_CONNECTION_REFUSAL,
    needsSwitch: "sync",
  },
  {
    key: "sync.view",
    area: "sync",
    who: "admins",
    label: "See the sync's status, its held items and the records it read",
    refusal: SYNC_VIEWS_REFUSAL,
    needsSwitch: "sync",
  },
  {
    key: "sync.staff",
    area: "sync",
    who: "admins",
    label: "Match Steadfast Apps staff to people in Cova",
    refusal: SYNC_VIEWS_REFUSAL,
    needsSwitch: "sync",
  },
  {
    key: "sync.classes",
    area: "sync",
    who: "admins",
    label: "Set up the firm's classes from Steadfast Apps, and move records onto them",
    refusal: "Only an administrator can set up the firm's classes.",
    needsSwitch: "sync",
  },
  // `requireMapper` keeps its own `ReferenceMappingForbidden`.
  {
    key: "sync.mapping",
    area: "sync",
    who: "admins",
    label: "Decide what a Steadfast Apps value means in the firm's lists",
    needsSwitch: "sync",
  },
  // The send, the review and the money keep keep their own rules and sentences.
  {
    key: "sync.send",
    area: "sync",
    who: "admins",
    label: "Send a delete, a posting or a booking to Steadfast Apps",
    needsSwitch: "sync",
  },
  {
    key: "sync.review",
    area: "sync",
    who: "admins",
    label: "Approve, decline or put back a held delete or posting",
    needsSwitch: "sync",
  },
  {
    key: "sync.keepMoney",
    area: "sync",
    who: "admins",
    label: "Keep Cova's value on a money field when the two sides differ",
    needsSwitch: "sync",
  },

  // ─── Moving to Cova: the firm's own admin, never Cova staff ─────────────
  {
    key: "move.agree",
    area: "move",
    who: "admins",
    admin: "firm",
    label: "Agree to the firm's move to Cova",
    refusal: "Only an administrator of this firm can agree to its move.",
  },
  {
    key: "move.matchProposal",
    area: "move",
    who: "admins",
    admin: "firm",
    label: "Read the client match proposal",
    refusal: "Only an administrator of this firm can read the client match proposal.",
  },
  {
    key: "move.remap",
    area: "move",
    who: "admins",
    admin: "firm",
    label: "Move documents between clients",
    refusal: "Only an administrator of this firm can move documents between clients.",
  },

  // ─── Firm-wide jobs: gated here, not listed on the page (Tai, question 6) ─
  {
    key: "jobs.placeClaims",
    area: "jobs",
    who: "admins",
    label: "Place unstaged claims on the board",
    refusal:
      "Placing unstaged claims on the board is an administrator action, because it covers the whole firm.",
  },
  {
    key: "jobs.renewalSweep",
    area: "jobs",
    who: "admins",
    keysAndJobs: true,
    label: "Run or preview the renewal sweep",
    refusal: "Running the renewal sweep is an administrator action, because it covers the whole firm.",
  },
  {
    key: "jobs.reminderSweep",
    area: "jobs",
    who: "admins",
    keysAndJobs: true,
    label: "Run the reminder sweep",
    refusal: "Running the reminder sweep is an administrator action, because it covers the whole firm.",
  },
  // Decision 048 ruling (l): a firm key keeps it only through the policies
  // write scope it is published under, and a job is refused.
  {
    key: "jobs.backfillPanels",
    area: "jobs",
    who: "admins",
    keyScope: "bms:policies:write",
    label: "Backfill policy panels",
    refusal: "Backfilling the panels is an administrator action, because it covers the whole firm.",
  },
] as const satisfies ReadonlyArray<FirmPermission>

export type FirmPermissionKey = (typeof FIRM_PERMISSIONS)[number]["key"]

/** The keys `requirePermission` refuses with the entry's own sentence. */
export type RefusableFirmPermissionKey = Extract<(typeof FIRM_PERMISSIONS)[number], { readonly refusal: string }>["key"]

/**
 * Who is asking, as both sides map it: the server from `BmsActor`
 * (`permissionActorOf` in `principal.ts`), the browser from the identity
 * (`useMayDo`). `admin` is the firm's admin or Cova staff acting inside the
 * firm; `firmAdmin` is the firm's own admin only. No identity has `firmAdmin`
 * without `admin`, and a member has neither.
 */
export type PermissionActor =
  | { readonly kind: "person"; readonly admin: boolean; readonly firmAdmin: boolean }
  /** `scopes` are the key's grant, read only by an entry's `keyScope`; absent reads as none. */
  | { readonly kind: "key"; readonly scopes?: ReadonlySet<string> }
  | { readonly kind: "job" }

const BY_KEY: ReadonlyMap<string, FirmPermission> = new Map(
  (FIRM_PERMISSIONS as ReadonlyArray<FirmPermission>).map((entry) => [entry.key, entry]),
)

const REFUSALS = Object.fromEntries(
  (FIRM_PERMISSIONS as ReadonlyArray<FirmPermission>).flatMap((entry) =>
    entry.refusal === undefined ? [] : [[entry.key, entry.refusal] as const],
  ),
) as Readonly<Record<RefusableFirmPermissionKey, string>>

/** The sentence the server refuses `key` with. */
export const refusalOf = (key: RefusableFirmPermissionKey): string => REFUSALS[key]

/**
 * Whether `actor` may do `key`. Everyone passes an `everyone` row; an `admins`
 * row asks `admin`, or `firmAdmin` on a firm's-own-admin row; a firm key or a
 * job passes an `admins` row only when it says `keysAndJobs`, and a firm key
 * also when it holds the row's `keyScope`. A key this build does not hold
 * refuses.
 */
export const mayDo = (actor: PermissionActor, key: FirmPermissionKey): boolean => {
  const entry = BY_KEY.get(key)
  if (entry === undefined) return false
  if (entry.who === "everyone") return true
  if (actor.kind === "key" && entry.keyScope !== undefined && actor.scopes?.has(entry.keyScope) === true) return true
  if (actor.kind !== "person") return entry.keysAndJobs === true
  return entry.admin === "firm" ? actor.firmAdmin : actor.admin
}
